/** IT partner portal (/partner, issue #27). Merged into en.ts under "partner". */
export const partnerEn = {
  badge: "Partner",
  nav: {
    dashboard: "Overview",
    customers: "Customers",
    billing: "Billing",
    activity: "Activity",
    team: "Team",
    account: "My account",
    signOut: "Sign out",
  },
  auth: {
    loginTitle: "Partner portal",
    loginSubtitle: "Manage the contacts of all your customers in one place.",
    email: "Email",
    password: "Password",
    passwordHint: "At least 12 characters.",
    passwordConfirm: "Repeat password",
    passwordMismatch: "The passwords do not match.",
    signIn: "Sign in",
    passkey: "Sign in with a passkey",
    or: "or",
    forgot: "Forgot password?",
    noAccount: "No partner account yet?",
    register: "Register as a partner",
    customerLogin: "Customers sign in with Microsoft",
    codeTitle: "Enter your code",
    codeSubtitle: "The 6-digit code from your authenticator app.",
    code: "Code",
    backupCode: "Backup code",
    useBackup: "Use a backup code",
    useApp: "Use the authenticator app",
    back: "Back",
    registerTitle: "Register as a partner",
    registerSubtitle:
      "For IT partners who look after Microsoft 365 for their customers. Free of charge, you pay only for customers billed through you.",
    inviteTitle: "Join {{partner}}",
    inviteSubtitle: "Create your account for the partner portal.",
    company: "Company",
    name: "Your name",
    language: "Language",
    create: "Create account",
    haveAccount: "Already registered?",
    checkEmailTitle: "Check your inbox",
    checkEmail: "We sent a confirmation link to {{email}}. After confirming, set up your second factor.",
    inviteDone: "Your account is ready. Sign in now.",
    forgotTitle: "Reset password",
    forgotSubtitle: "We send you a link to set a new password.",
    sendLink: "Send link",
    linkSent: "If an account exists for this address, the link is on its way.",
    resetTitle: "Set a new password",
    resetDone: "Your password was changed. Sign in with the new password.",
    save: "Save",
    errors: {
      invalid_email_or_password: "Email or password is wrong.",
      email_not_verified: "Please confirm your email address first. We just sent you the link again.",
      invalid_code: "The code is wrong or expired.",
      invalid_two_factor_code: "The code is wrong or expired.",
      invalid_backup_code: "This backup code is wrong or already used.",
      company_name_required: "Please enter your company name.",
      invalid_invitation: "This invitation is invalid or expired, or it was sent to another address.",
      password_too_short: "The password needs at least 12 characters.",
      invalid_token: "The link is invalid or expired. Request a new one.",
      too_many_requests: "Too many attempts. Wait a moment and try again.",
      default: "That did not work. Please try again.",
    },
  },
  secondFactor: {
    requiredTitle: "Set up a second factor",
    required:
      "Partner accounts reach the data of many customers. Before you continue, protect your account with an authenticator app or a passkey.",
    totpTitle: "Authenticator app",
    totpText: "Codes from an app such as Microsoft Authenticator or 1Password.",
    totpOn: "Active",
    setUp: "Set up",
    confirmPassword: "Confirm with your password",
    scan: "Scan the QR code with your authenticator app, then enter the current code.",
    qrAlt: "QR code for the authenticator app",
    secret: "Or enter this key manually",
    verify: "Confirm",
    backupTitle: "Store your backup codes",
    backupText:
      "Each code works once, if you lose your phone. Store them somewhere safe, for example in your password manager.",
    download: "Download",
    done: "Done",
    passkeysTitle: "Passkeys",
    passkeysText: "Sign in with your fingerprint, face or device PIN. A passkey replaces password and code.",
    addPasskey: "Add passkey",
    noPasskeys: "No passkey yet.",
    removePasskey: "Remove",
    passkeyAdded: "Passkey added.",
    passkeyRemoved: "Passkey removed.",
    totpEnabled: "The authenticator app is set up.",
  },
  account: {
    title: "My account",
    profile: "Profile",
    role: "Role",
    partnerCode: "Partner code",
    partnerCodeHint:
      "Your customers enter this code in Kontaktfix under Settings, Partner to give you access.",
    changePassword: "Change password",
    currentPassword: "Current password",
    newPassword: "New password",
    passwordChanged: "Password changed. Other sessions were signed out.",
  },
  roles: { admin: "Partner admin", technician: "Technician" },
  customerRoles: { admin: "Administrator", editor: "List editor", none: "No access" },
  customers: {
    title: "Customers",
    description:
      "Every customer is its own Microsoft 365 tenant. Open a customer to work in it with your role.",
    add: "Add customer",
    empty: "No customers yet",
    emptyText:
      "Add your first customer by its Microsoft 365 domain. If the customer does not use Kontaktfix yet, you set it up directly.",
    emptyTechnician:
      "No customers are assigned to you yet. Your partner admin assigns customers in the team.",
    name: "Customer",
    status: "Status",
    connection: "Microsoft 365",
    myRole: "Your role",
    open: "Open",
    pending: "Awaiting approval",
    active: "Active",
    connected: "Connected",
    notConnected: "Not connected",
    remove: "Remove customer",
    removeTitle: "Remove {{name}}?",
    removeText:
      "Your team loses access to this customer at once. The customer's contacts and settings stay as they are.",
    removed: "Customer removed.",
    addTitle: "Add customer",
    addText:
      "Enter a domain of the customer's Microsoft 365 tenant, for example muster.ch or muster.onmicrosoft.com.",
    domain: "Domain",
    customerName: "Customer name",
    customerLanguage: "Language for employees",
    addSubmit: "Add",
    addedActive: "Customer created. Now connect Microsoft 365 as the customer's Global Administrator.",
    addedPending:
      "This customer already uses Kontaktfix. Its administrators got a request to approve your access.",
  },
  team: {
    title: "Team",
    description:
      "Partner admins manage customers, the team and billing and are administrators in every customer. Technicians work only in the customers you assign to them.",
    invite: "Invite",
    inviteTitle: "Invite to the team",
    inviteText:
      "The person gets an email with a link and creates their own account with password and second factor.",
    invited: "Invitation sent.",
    pendingInvitations: "Open invitations",
    expires: "valid until {{date}}",
    revoke: "Withdraw",
    allCustomers: "All customers",
    chooseForAll: "Set for all…",
    allChanged_one: "{{count}} customer changed.",
    allChanged_other: "{{count}} customers changed.",
    resend: "Send again",
    resent: "Invitation sent again. The new link is valid for 7 days.",
    emailLanguage: "Language of the invitation",
    member: "Person",
    role: "Role",
    secondFactor: "Second factor",
    access: "Customer access",
    accessAll: "All customers",
    accessCount_one: "{{count}} customer",
    accessCount_other: "{{count}} customers",
    edit: "Customer access",
    makeAdmin: "Make partner admin",
    makeTechnician: "Make technician",
    remove: "Remove from team",
    removeTitle: "Remove {{name}}?",
    removeText: "The account is deleted and loses access to every customer at once.",
    you: "You",
    accessTitle: "Customer access for {{name}}",
    accessText:
      "Per customer: administrator, list editor or no access. Every change is recorded in the customer's history.",
    noCustomers: "No active customers yet.",
  },
  dashboard: {
    title: "Overview",
    description: "All your customers at a glance. Customers that need a look come first.",
    customers: "Active customers",
    pendingCount_one: "{{count}} awaiting approval",
    pendingCount_other: "{{count}} awaiting approval",
    connectedCount_one: "{{count}} connected to Microsoft 365",
    connectedCount_other: "{{count}} connected to Microsoft 365",
    people: "People",
    peopleHint: "who receive contacts",
    contacts: "Contacts",
    monthly: "Billed to you",
    monthlyHint: "per month, excluding VAT",
    attention: "Need a look",
    overview: "Customers",
    attentionCount_one: "{{count}} customer needs a look.",
    attentionCount_other: "{{count}} customers need a look.",
    allGood: "Everything runs.",
    state: "Status",
    lastSync: "Last sync",
    never: "Not yet",
    problems: {
      ok: "Runs",
      pending: "Awaiting approval",
      billing: "No paid access",
      connectionError: "Connection lost",
      notConnected: "Microsoft 365 not connected",
      syncFailed: "Last sync failed",
    },
  },
  billing: {
    title: "Billing",
    description:
      "Decide per customer who pays: the customer itself or you. You receive one invoice for all customers billed to you, with every customer as its own line.",
    payment: "Payment",
    ready: "Ready",
    notReady: "Not set up",
    pastDue: "Payment failed",
    notConfigured: "Billing is not configured on this server.",
    byCard: "You pay by card. Stripe charges the invoice every month.",
    noCard:
      "No payment method yet. Customers billed to you have no paid access until you add a card or pay by invoice.",
    byInvoice: "You pay monthly by invoice, due within {{days}} days. Stripe sends the invoice by email.",
    invoiceRequested: "Your application for payment by invoice is being reviewed.",
    invoiceRejected: "Payment by invoice was not approved.",
    pastDueText: "The last payment failed. Please check your payment method in Invoices and payment.",
    billedCustomers: "Customers billed to you",
    pricePerUser: "Price per person",
    perUserMonth: "per person and month",
    total: "Monthly total",
    totalHint: "at the current number of people, excl. VAT",
    addCard: "Add card",
    changeCard: "Change card",
    applyInvoice: "Pay by invoice",
    portal: "Invoices and payment",
    waitingForStripe: "Confirming with Stripe…",
    cardSaved: "Payment method saved.",
    customers: "Who pays",
    customersText:
      "The change applies at once and is recorded in the customer's history. The customer sees it on its billing page.",
    monthly: "Per month",
    billedTo: "Billed to",
    toCustomer: "Customer",
    toPartner: "You",
    billedToPartner: "You pay",
    changed: "Billing changed.",
    endsAt: "You pay until {{date}}, then the customer pays.",
    ownUntil: "The customer's own subscription runs until {{date}}, then you pay.",
    trialUntil: "Free trial until {{date}}, then you pay.",
    trialPending: "The free trial starts with the first delivery.",
    customerSubscribed: "Own subscription",
    setAll: "Set for all…",
    allToPartner: "All billed to you",
    allToCustomer: "All billed to the customer",
    allChanged_one: "Billing changed for {{count}} customer.",
    allChanged_other: "Billing changed for {{count}} customers.",
    reports: "Monthly reports",
    reportsText:
      "On the 1st of every month, with every customer, people, contacts and amounts. Partner admins also receive it by email.",
    noReports: "The first report comes on the 1st of next month.",
    reportSummary_one: "{{count}} customer, {{total}} per month",
    reportSummary_other: "{{count}} customers, {{total}} per month",
    howTitle: "How partner billing works",
    howPrice:
      "Same price as for customers: CHF 2 per person who receives contacts, per month. Every customer gets its {{days}}-day free trial; you pay from its end.",
    howSwitch:
      "If a customer with its own subscription switches to you, its subscription ends at the end of the paid period. Then you pay.",
    howEnd:
      "If you switch a customer back or the link ends, you pay until the end of the current period. Then the customer needs its own subscription.",
    applyTitle: "Apply for payment by invoice",
    applyText:
      "Instead of a card, you pay monthly by invoice, due within {{days}} days. We review the application and confirm it by email.",
    invoiceEmail: "Email for invoices",
    street: "Street and number",
    postalCode: "Postal code",
    city: "City",
    country: "Country",
    countries: { CH: "Switzerland", LI: "Liechtenstein", DE: "Germany", AT: "Austria" },
    applySubmit: "Apply",
    applied: "Application sent. We will get back to you by email.",
  },
  activity: {
    title: "Activity",
    description:
      "What your team changed in your customers, from the customers' history. Every customer also sees these entries itself.",
    person: "Person",
    everyone: "Whole team",
    empty: "No changes yet.",
  },
  banner: {
    working: "You work in {{org}} as {{partner}}.",
    portal: "Partner portal",
    switch: "Switch customer",
  },
};
